import React, { useState, useEffect } from 'react';
import { Breadcrumb, Image, Select } from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import ActivitySwitch from 'modules/layoutModule/components/ActivitySwitch/ActivitySwitch';
import {
  Form,
  Input,
  Button,
  Space,
  Skeleton,
  ConfigProvider,
  BackTop,
} from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { useHistory } from 'react-router-dom';
import plPL from 'antd/lib/locale/pl_PL';
import 'moment/locale/pl';
import {
  getSpecificBannerRequest,
  updateSpecificBannerRequest,
} from 'modules/bannersModule/requests/bannersRequests';
import { StaticImagesFallbackUrl } from 'modules/layoutModule/consts/StaticImagesFallbackUrl';
import staticUrl from 'helpers/staticUrl';
import FormCoverUpload from 'modules/galleryModules/components/FormCoverUpload';

const BannersEdit = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [fields, setFields] = useState<FieldData[]>([]);
  const [loading, toggleLoading] = useState<boolean>(false);
  const history = useHistory();
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [isActivityDisabled, toggleActivityDisable] = useState(false);
  const [fileImage1, setFileImage1] = useState('');
  const [fileImage2, setFileImage2] = useState('');
  const [fileImage1Changed, changedFileImage1] = useState<boolean>(false);
  const [fileImage2Changed, changedFileImage2] = useState<boolean>(false);

  useEffect(() => {
    toggleLoading(true);
    getSpecificBannerRequest(id)
      .then((response) => {
        if (response) {
          toggleLoading(false);
          const { data } = response;

          setFields([
            {
              name: ['id'],
              value: data.id,
            },
            {
              name: ['name'],
              value: data.name,
            },
            {
              name: ['bannerURL'],
              value: data.bannerURL,
            },
            {
              name: ['target'],
              value: data.target,
            },
            {
              name: ['fileImage1'],
              value: data.fileImage1,
            },
            {
              name: ['fileImage2'],
              value: data.fileImage1Mobile,
            },
            {
              name: ['isActive'],
              value: data.isActive || false,
            },
          ]);
          setFileImage1(data.fileImage1);
          setFileImage2(data.fileImage1Mobile);
        } else {
          history.push('/banners');
        }
      })
      .catch(() => {
        history.push('/banners');
      });
  }, []);

  const onFinish = (values: any) => {
    toggleButtonLoading(true);
    updateSpecificBannerRequest(values).then(() => {
      toggleButtonLoading(false);
    });
  };

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const updateCover = (
    e: string,
    field: string,
    coverMethod: (value: string) => void,
    coverMethodChanged: (value: boolean) => void
  ) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
    coverMethod(e);
    coverMethodChanged(true);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Wiedza</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/banners">Reklamy banerowe</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/banners/${id}`}>Edycja wpisu</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <div className="site-layout-background">
          {loading && <Skeleton active />}
          {!loading && (
            <Form
              layout="vertical"
              fields={fields}
              onFinish={onFinish}
              form={form}
              validateMessages={validateMessages}
            >
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="name" label="Lokalizacja">
                <Image
                  width={142}
                  src={`${staticUrl()}banery/${id}.jpg`}
                  fallback={StaticImagesFallbackUrl}
                  preview={false}
                />
              </Form.Item>
              <Form.Item
                name="fileImage1"
                label="Desktop"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <FormCoverUpload
                  cover={fileImage1}
                  setCover={(e) =>
                    updateCover(
                      e,
                      'fileImage1',
                      setFileImage1,
                      changedFileImage1
                    )
                  }
                  isNew={fileImage1Changed}
                  id={form.getFieldsValue()?.id}
                  field="fileImage1"
                  number={1}
                  hideTags
                  originalFile
                />
              </Form.Item>
              <Form.Item
                name="fileImage2"
                label="Mobile"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <FormCoverUpload
                  cover={fileImage2}
                  setCover={(e) =>
                    updateCover(
                      e,
                      'fileImage2',
                      setFileImage2,
                      changedFileImage2
                    )
                  }
                  isNew={fileImage2Changed}
                  id={form.getFieldsValue()?.id}
                  field="fileImage2"
                  number={2}
                  hideTags
                  originalFile
                />
              </Form.Item>
              <Form.Item
                name="name"
                label="Kampania"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="bannerURL" label="Url">
                <Input />
              </Form.Item>
              <Form.Item name="target" label="Target">
                <Select
                  showSearch
                  placeholder="Wybierz jedną z opcji"
                  optionFilterProp="children"
                  options={[
                    { label: '', value: '' },
                    { label: '_self', value: '_self' },
                    { label: '_blank', value: '_blank' },
                  ]}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>
              <ActivitySwitch isActivityDisabled={isActivityDisabled} />
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isSaveButtonLoading}
                  >
                    Zapisz
                  </Button>
                  <Link to="/banners">
                    <Button>Powrót</Button>
                  </Link>
                </Space>
              </Form.Item>
            </Form>
          )}
          {loading && (
            <Link to="/banners">
              <Button>Powrót</Button>
            </Link>
          )}
          <BackTop />
        </div>
      </ConfigProvider>
    </>
  );
};

export default BannersEdit;
